'use client';

import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: string;
  name: string;
  description: string;
  link: string | { url: string; label: string }[];
  logo: string;
  category: string;
  highlights: string[];
  bestFor: string[];
}

interface ResourceCardProps {
  resource: Resource;
}

const ResourceCard = ({ resource }: ResourceCardProps) => {
  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-200"
      style={{
        background: '#0F2234',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.30)',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.border = '1px solid rgba(111,211,224,0.25)';
        el.style.boxShadow = '0 8px 30px rgba(0,0,0,0.45)';
        el.style.transform = 'translateY(-3px)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.border = '1px solid rgba(255,255,255,0.08)';
        el.style.boxShadow = '0 4px 20px rgba(0,0,0,0.30)';
        el.style.transform = 'translateY(0)';
      }}
    >
      {/* Header with Logo */}
      <div
        className="p-6"
        style={{
          background: 'rgba(111,211,224,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-20 h-20 bg-white rounded-lg shadow-md p-2 flex items-center justify-center">
            <Image
              src={resource.logo}
              alt={`${resource.name} logo`}
              width={80}
              height={80}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <div
              className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-2"
              style={{
                background: 'rgba(111,211,224,0.12)',
                color: '#6FD3E0',
                border: '1px solid rgba(111,211,224,0.2)',
              }}
            >
              {resource.category}
            </div>
            <h3 className="text-xl font-headline font-bold" style={{ color: '#EAF4F8' }}>
              {resource.name}
            </h3>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="p-6">
        <p className="leading-relaxed mb-6" style={{ color: '#C8DDE6' }}>
          {resource.description}
        </p>

        {/* Highlights */}
        <div className="mb-6">
          <h4
            className="text-sm font-semibold mb-3 flex items-center gap-2"
            style={{ color: '#EAF4F8' }}
          >
            <Icon name="StarIcon" size={16} style={{ color: '#FF8A3D' }} />
            Why this resource matters
          </h4>
          <ul className="space-y-2">
            {resource.highlights.map((highlight, index) => (
              <li key={index} className="text-sm flex items-start gap-2" style={{ color: '#C8DDE6' }}>
                <span className="flex-shrink-0 mt-0.5">{highlight}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Best For */}
        <div className="mb-6">
          <h4
            className="text-sm font-semibold mb-3 flex items-center gap-2"
            style={{ color: '#EAF4F8' }}
          >
            <Icon name="CheckCircleIcon" size={16} style={{ color: '#6FD3E0' }} />
            Best for
          </h4>
          <ul className="space-y-2">
            {resource.bestFor.map((item, index) => (
              <li key={index} className="text-sm flex items-start gap-2" style={{ color: '#C8DDE6' }}>
                <span className="flex-shrink-0 mt-0.5">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Visit Link — Primary CTA */}
        {typeof resource.link === 'string' ? (
          <a
            href={resource.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200"
            style={{ background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#5ec4d1'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#6FD3E0'; }}
          >
            <span>Visit Resource</span>
            <Icon name="ArrowTopRightOnSquareIcon" size={20} />
          </a>
        ) : (
          <div className="space-y-3">
            {resource.link.map((linkItem, index) => (
              <a
                key={index}
                href={linkItem.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200"
                style={{ background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#5ec4d1'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#6FD3E0'; }}
              >
                <span>{linkItem.label}</span>
                <Icon name="ArrowTopRightOnSquareIcon" size={20} />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceCard;
